/**
 * Données dérivées de l'état.
 *
 * Fonctions pures : même état et même instant donnent toujours le même
 * résultat. C'est ici que se décide l'ordre d'affichage des feux, et c'est la
 * seule couche où la proximité d'un feu est autorisée à modifier un verdict.
 *
 * Un principe, et un seul : la proximité ne peut que DÉGRADER. Aucune règle de
 * ce fichier ne peut rendre un verdict plus rassurant qu'il ne l'était.
 */
import {
  FIRE_ANY_DIRECTION_KM,
  FIRE_CLOSE_KM,
  FIRE_REACHING_KM,
  NEARBY_KM,
  SENSOR_TRUST_KM,
} from '../core/constants';
import { directionTo } from '../core/format';
import { distanceKm, localFrame, median } from '../core/geo';
import { assessPlume, type PlumeContext } from '../core/plume';
import type { FireZone, PlumeAssessment, Verdict, VerdictBand } from '../core/types';
import {
  bandByKey,
  bandSeverity,
  decide,
  hasLocalMeasurement,
  localSensors,
} from '../core/verdict';
import { allRegimes } from '../core/wind';
import { isRealFire, isWeakDetection } from '../core/zones';
import type { AppState } from './store';

export interface ZoneWithPlume {
  zone: FireZone;
  plume: PlumeAssessment | null;
  distanceKm: number;
}

export interface NearbyFires {
  /** Feux à moins de 100 km, ou susceptibles de nous atteindre même au-delà. */
  nearby: ZoneWithPlume[];
  /**
   * Sources thermiques industrielles permanentes, écartées du verdict.
   *
   * Torchères, aciéries, cimenteries, incinérateurs : elles brûlent tous les
   * jours de l'année et ne sont pas dessinées sur la carte.
   */
  industrial: ZoneWithPlume[];
  /**
   * Signaux trop faibles pour être annoncés comme des incendies.
   *
   * Relevé avec les données FIRMS : 29 des 49 zones d'une journée ordinaire.
   * Le cas type est une détection unique, confiance de 20 sur 100 et 3 MW
   * dégagés, là où l'incendie du massif landais en donnait 1370 à confiance
   * 100 et 293 MW. Un feu de jardin dégage 3 MW.
   *
   * Elles restent visibles sur la carte, en petit, parce qu'un incendie
   * naissant commence forcément petit. Mais elles ne comptent pas, ne sont pas
   * listées et ne déclenchent aucune consigne.
   */
  weak: ZoneWithPlume[];
  /** Ceux dont le panache physique nous vise. Sert aux heures et à la carte. */
  inPlume: ZoneWithPlume[];
  /** Ceux qui pourraient nous atteindre vu l'incertitude. Sert à la prudence. */
  couldReach: ZoneWithPlume[];
  /** Le feu à mettre en avant : le premier dans l'axe, sinon le plus proche. */
  headline: ZoneWithPlume | null;
  /** Panache encore en chemin, avec une heure d'arrivée annonçable. */
  enRoute: ZoneWithPlume | null;
}

/** Contexte de calcul des panaches, à construire une fois par rendu. */
export function plumeContext(state: AppState, now: number): PlumeContext {
  const origin = { lat: state.position.lat, lon: state.position.lon };
  return {
    origin,
    frame: localFrame(origin),
    regimes: allRegimes(state.weather),
    now,
  };
}

export function selectNearbyFires(state: AppState, now: number): NearbyFires {
  const context = plumeContext(state, now);

  const all = state.zones.map((zone): ZoneWithPlume => {
    const plume = assessPlume(zone, context);
    return {
      zone,
      plume,
      /*
       * Distance au BORD du foyer, pas à son centre.
       *
       * `assessPlume` la calcule déjà depuis le point le plus proche. La
       * reprendre ici est indispensable : sans cela les planchers de prudence
       * raisonnaient sur le barycentre. Constaté sur l'incendie du massif
       * landais, large de 48 km : son centre était à 26 km de Mios, son front
       * bien plus près, et l'application affichait "Air correct".
       */
      distanceKm:
        plume?.distanceKm ??
        distanceKm(state.position.lat, state.position.lon, zone.lat, zone.lon),
    };
  });

  const inRange = (entry: ZoneWithPlume): boolean =>
    entry.distanceKm <= NEARBY_KM || entry.plume?.couldReach === true;

  /*
   * Les torchères sortent d'ici, et donc du verdict et du décompte.
   *
   * Un relevé réel donnait Fos-sur-Mer, Berre-l'Étang, Martigues, Vitrolles et
   * Gardanne le même jour, soit tout le bassin pétrochimique de l'étang de
   * Berre, présenté comme cinq incendies. Elles restent visibles sur la carte,
   * mais ne déclenchent plus aucune consigne.
   */
  const industrial = all
    .filter((entry) => !isRealFire(entry.zone) && entry.distanceKm <= NEARBY_KM)
    .sort((a, b) => a.distanceKm - b.distanceKm);

  const weak = all
    .filter((entry) => isRealFire(entry.zone) && isWeakDetection(entry.zone))
    .sort((a, b) => a.distanceKm - b.distanceKm);

  const nearby = all
    .filter((entry) => isRealFire(entry.zone) && !isWeakDetection(entry.zone) && inRange(entry))
    .sort((a, b) => {
      /*
       * Le classement DÉRIVE du même barème que les planchers de prudence.
       *
       * Sans cela les deux divergeaient : depuis Fos-sur-Mer, le verdict
       * annonçait "Feu tout près" à cause d'un foyer à moins de 10 km, pendant
       * que l'encadré détaillait Meyrueis à 127 km, simplement parce que ce
       * dernier était dans l'axe du vent. Faire dériver le tri du barème rend
       * cette contradiction impossible.
       */
      const threat = threatRank(b) - threatRank(a);
      if (threat !== 0) return threat;

      const established =
        Number(b.plume?.established ?? false) - Number(a.plume?.established ?? false);
      if (established !== 0) return established;

      /*
       * Les deux heures d'arrivée doivent être comparées séparément, et non par
       * une soustraction avec Infinity en valeur de repli.
       *
       * Quand aucun feu n'a d'heure, `Infinity - Infinity` vaut NaN, le
       * comparateur renvoie NaN, et le tableau reste dans un ordre arbitraire.
       * Constaté en conditions réelles : l'application annonçait "le feu le
       * plus proche est à 38 km" alors que la liste contenait un feu à 30 km.
       */
      const arrivalA = a.plume?.arrival ?? null;
      const arrivalB = b.plume?.arrival ?? null;
      if (arrivalA !== null && arrivalB === null) return -1;
      if (arrivalA === null && arrivalB !== null) return 1;
      if (arrivalA !== null && arrivalB !== null && arrivalA !== arrivalB) {
        return arrivalA - arrivalB;
      }

      return a.distanceKm - b.distanceKm;
    });

  const inPlume = nearby.filter((entry) => entry.plume?.toward === true);
  const couldReach = nearby.filter((entry) => entry.plume?.couldReach === true);
  const enRoute =
    inPlume
      .filter((entry) => entry.plume?.arrival != null)
      .sort((a, b) => (a.plume?.arrival ?? 0) - (b.plume?.arrival ?? 0))[0] ?? null;

  return {
    nearby,
    industrial,
    weak,
    inPlume,
    couldReach,
    headline: couldReach[0] ?? nearby[0] ?? null,
    enRoute,
  };
}

/* ---------------------------------------------------------------------------
   Plancher de prudence

   Aucune mesure prise à 20 km ne renseigne sur l'air d'un salon quand un feu
   brûle à 16 km, et le modèle CAMS, de maille voisine de 10 km, ne voit pas
   davantage un panache frais. Sans ce plancher, l'application concluait
   "les capteurs ne voient pas de fumée" et autorisait à aérer.
--------------------------------------------------------------------------- */

export type FloorReason =
  /** Feu susceptible de nous atteindre, et proche. */
  | 'in-axis-close'
  /** Feu si proche que la direction du vent ne protège plus de rien. */
  | 'very-close'
  /** Vent inexploitable : la direction n'apporte aucune information. */
  | 'no-wind-info'
  /** Feu susceptible de nous atteindre, à distance moyenne. */
  | 'in-axis-reaching';

export interface FireFloor {
  band: VerdictBand;
  entry: ZoneWithPlume;
  reason: FloorReason;
}

/** Priorité entre planchers, du plus grave au moins grave. */
const FLOOR_RANK: Readonly<Record<FloorReason, number>> = {
  'in-axis-close': 4,
  'very-close': 3,
  'no-wind-info': 2,
  'in-axis-reaching': 1,
};

/** Gravité du plancher qu'un feu déclencherait, ou 0 s'il n'en déclenche aucun. */
function threatRank(entry: ZoneWithPlume): number {
  const floor = floorFor(entry);
  return floor ? FLOOR_RANK[floor.reason] : 0;
}

function floorFor(entry: ZoneWithPlume): FireFloor | null {
  const km = entry.distanceKm;
  const reaches = entry.plume?.couldReach === true;
  const noWindInfo = entry.plume?.weakWind === true;

  if (reaches && km <= FIRE_CLOSE_KM) {
    return { band: bandByKey('no'), entry, reason: 'in-axis-close' };
  }

  /*
   * Quelle que soit la direction annoncée. À dix kilomètres, une rotation du
   * vent ramène le panache en quelques minutes, et l'incertitude du modèle
   * interdit d'affirmer que le feu est hors de l'axe.
   */
  if (km <= FIRE_ANY_DIRECTION_KM) {
    return { band: bandByKey('no'), entry, reason: 'very-close' };
  }

  /*
   * Vent trop faible pour avoir une direction exploitable.
   *
   * Ne pas savoir d'où vient l'air n'autorise pas à écarter un feu : cela
   * retire simplement toute information directionnelle, et il ne reste que la
   * distance. Un air stagnant ne disperse pas la fumée, il la laisse s'accumuler
   * et dériver sans qu'on puisse dire vers où.
   *
   * Cas constaté à Mios : douze détections entre 16 et 32 km, vent à 3 km/h, et
   * l'application affichait "Air correct" sans mentionner le moindre risque.
   */
  if (noWindInfo && km <= FIRE_CLOSE_KM) {
    return { band: bandByKey('short'), entry, reason: 'no-wind-info' };
  }

  if (reaches && km <= FIRE_REACHING_KM) {
    return { band: bandByKey('short'), entry, reason: 'in-axis-reaching' };
  }

  return null;
}

/** Le plancher le plus contraignant imposé par les feux détectés. */
export function selectFireFloor(fires: NearbyFires): FireFloor | null {
  let best: FireFloor | null = null;

  for (const entry of fires.nearby) {
    const candidate = floorFor(entry);
    if (!candidate) continue;
    if (!best) {
      best = candidate;
      continue;
    }
    const rank = FLOOR_RANK[candidate.reason];
    const bestRank = FLOOR_RANK[best.reason];
    if (rank > bestRank) best = candidate;
    else if (rank === bestRank && candidate.entry.distanceKm < best.entry.distanceKm) {
      best = candidate;
    }
  }

  return best;
}

/**
 * Les titres énoncent un FAIT sur le feu, jamais une conclusion sur l'air.
 *
 * "Fumée probable ici" affirmait une probabilité que rien ne permet de
 * calculer. Quand aucun capteur n'est assez proche, l'application constate la
 * présence d'un feu et l'absence de mesure : c'est tout ce qu'elle sait.
 */
function precautionTitle(reason: FloorReason): string {
  switch (reason) {
    case 'in-axis-close':
      return "Feu proche, dans l'axe du vent";
    case 'very-close':
      return 'Feu tout près';
    case 'no-wind-info':
      return 'Feu proche, vent faible';
    case 'in-axis-reaching':
      return 'Feu dans le secteur';
  }
}

function situationText(floor: FireFloor): string {
  const km = Math.round(floor.entry.distanceKm);
  const name = floor.entry.zone.place?.label;
  const where = floor.entry.plume ? ` ${directionTo(floor.entry.plume.bearing)}` : '';
  const place = name ? `Feu à ${km} km${where}, vers ${name}.` : `Feu à ${km} km${where}.`;

  switch (floor.reason) {
    case 'very-close':
      return `${place} À cette distance, le vent peut tourner vite.`;
    case 'no-wind-info':
      return `${place} Vent trop faible pour savoir où part la fumée.`;
    default:
      return `${place} Le vent peut porter sa fumée jusqu'ici.`;
  }
}

/**
 * On s'arrête au constat, sans recommander.
 *
 * La phrase finale disait "Par précaution, mieux vaut garder fermé" : un
 * conseil sans la mesure qui le justifie. Dire qu'on ne sait pas suffit.
 */
function precautionReason(floor: FireFloor, verdict: Verdict): string {
  const evidence =
    verdict.nearestSensorKm !== null
      ? `Capteur le plus proche à ${Math.round(verdict.nearestSensorKm)} km : ` +
        "on ne sait pas ce qu'il y a dans l'air ici."
      : "Aucun capteur près d'ici : on ne sait pas ce qu'il y a dans l'air.";

  return `${situationText(floor)} ${evidence}`;
}

export interface DisplayedVerdict {
  /** Le verdict issu des seules mesures, conservé tel quel. */
  verdict: Verdict;
  /** La bande finale, après application du plancher de prudence. */
  band: VerdictBand;
  title: string;
  colorVar: string;
  reason: string;
  /** Le feu qui a imposé le plancher, ou le plus menaçant s'il n'y a pas eu de plancher. */
  trigger: ZoneWithPlume | null;
  /** Vrai quand le plancher a réellement aggravé le verdict mesuré. */
  precautionary: boolean;
}

/**
 * Le verdict tel qu'il s'affiche.
 *
 * Ordre de priorité :
 *
 *   1. Un signalement d'odeur récent. Déjà au maximum, rien ne l'aggrave.
 *   2. Une VRAIE mesure locale, c'est-à-dire au moins deux capteurs frais à
 *      moins de 10 km. Elle constate l'air réellement respiré, et aucune
 *      géométrie ne peut la contredire.
 *   3. À défaut, le plancher de prudence imposé par les feux voisins.
 *   4. À défaut, le verdict mesuré ou modélisé.
 */
export function selectDisplayedVerdict(
  state: AppState,
  fires: NearbyFires,
  now: number,
): DisplayedVerdict {
  const verdict = decide({
    sensors: state.sensors,
    air: state.air,
    nose: state.nose,
    now,
  });

  const base: DisplayedVerdict = {
    verdict,
    band: verdict.band,
    title: verdict.band.title,
    colorVar: verdict.band.colorVar,
    reason: verdict.reason,
    trigger: fires.couldReach[0] ?? null,
    precautionary: false,
  };

  // 1. Le nez a déjà tout emporté.
  if (verdict.basis === 'nose') return base;

  const floor = selectFireFloor(fires);
  if (!floor) return base;

  // 2. Une mesure locale lève le plancher, mais le feu reste signalé ailleurs.
  if (hasLocalMeasurement(state.sensors, now)) {
    return { ...base, trigger: floor.entry };
  }

  // 3. Le plancher s'applique s'il aggrave, ou si l'on ne sait rien.
  const measuredRank = bandSeverity(verdict.band.key);
  const floorRank = bandSeverity(floor.band.key);
  const unknownAir = verdict.band.key === 'unknown';

  if (floorRank < measuredRank && !unknownAir) {
    return { ...base, trigger: floor.entry };
  }

  return {
    verdict,
    band: floor.band,
    title: precautionTitle(floor.reason),
    colorVar: floor.band.colorVar,
    reason: precautionReason(floor, verdict),
    trigger: floor.entry,
    precautionary: true,
  };
}

export interface FireListEntry {
  entry: ZoneWithPlume;
  /** Nombre de foyers regroupés sous ce même nom de commune. */
  count: number;
}

/**
 * La liste telle qu'elle s'affiche, dédoublonnée par commune.
 *
 * Le regroupement des pixels produit parfois plusieurs foyers dans une même
 * commune, et la liste montrait alors "Lanton" deux fois, "Le Temple" deux
 * fois, "Biscarrosse" deux fois. Ce n'était pas faux, mais illisible.
 *
 * Seul l'AFFICHAGE fusionne. Le calcul continue de voir toutes les zones :
 * un plancher de prudence doit pouvoir être déclenché par n'importe laquelle
 * d'entre elles, y compris une qui aurait été masquée ici.
 */
export function selectFireList(fires: NearbyFires): FireListEntry[] {
  const byName = new Map<string, FireListEntry>();

  for (const entry of fires.nearby) {
    // Sans nom, chaque zone reste distincte : rien ne permet de les confondre.
    const key = entry.zone.place?.label ?? `#${entry.zone.id}`;
    const existing = byName.get(key);
    if (existing) existing.count += 1;
    // fires.nearby est déjà trié du plus menaçant au moins menaçant, donc le
    // premier rencontré est celui à mettre en avant.
    else byName.set(key, { entry, count: 1 });
  }

  return [...byName.values()];
}

export interface LocalAir {
  /** Médiane des capteurs proches, ou `null` s'il n'y en a aucun. */
  pm25: number | null;
  count: number;
  nearestKm: number | null;
  /** Vrai si l'échantillon est assez fourni pour trancher seul. */
  trustworthy: boolean;
}

/**
 * Ce que mesurent les capteurs réellement proches.
 *
 * Volontairement limité au rayon de confiance : c'est cette valeur, et non la
 * médiane sur 25 km, qui a le droit de contredire une alerte de proximité.
 */
export function selectLocalAir(state: AppState, now: number): LocalAir {
  const near = localSensors(state.sensors, now);
  return {
    pm25: near.length > 0 ? median(near.map((sensor) => sensor.pm25)) : null,
    count: near.length,
    nearestKm:
      near.length > 0 ? Math.min(...near.map((sensor) => sensor.distanceKm)) : null,
    trustworthy: hasLocalMeasurement(state.sensors, now),
  };
}

/** Rayon dans lequel une mesure est considérée comme représentative. */
export const TRUST_RADIUS_KM = SENSOR_TRUST_KM;
